import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { useAuth } from '@/contexts/use-auth'

/**
 * `POST /admin/auth/login` — email-2FA step 1 (DEC-ADMIN-011).
 *
 * Bearer-only mode (NEM SPA cookie). A 200 response NEM ad tokent: a backend
 * emailben elküld egy 6 jegyű kódot és visszaad egy `{ mfa_required, challenge,
 * expires_in }` envelope-ot. A tokenért a kódot a `/admin/auth/mfa/verify`-n
 * kell beváltani. Hibák:
 *  - 401 unauthenticated → hibás email/jelszó
 *  - 403 forbidden → disabled admin
 *  - 422 validation_failed → field-szintű
 *  - 429 rate_limited → too many attempts (`admin-login`, 5/min/IP — DEC-ADMIN-002)
 */
export function useLogin() {
  return useMutation({
    mutationFn: async (body: { email: string; password: string }) => {
      const { data, error } = await apiClient.POST('/admin/auth/login', { body })
      if (error) throw error
      if (!data) throw new Error('Empty response from /admin/auth/login')
      return data
    },
  })
}

/**
 * `POST /admin/auth/mfa/verify` — email-2FA step 2 (DEC-ADMIN-011).
 *
 * A `{ challenge, code }`-ot beváltja a valódi bearer tokenre; a 200 response
 * `{ data: AdminAuthUser, token }`. Hibák: 422 (rossz/lejárt kód vagy lejárt
 * challenge → `errors.code` / `errors.challenge`), 429 (`admin-mfa`, 10/min/IP).
 */
export function useVerifyMfa() {
  return useMutation({
    mutationFn: async (body: { challenge: string; code: string }) => {
      const { data, error } = await apiClient.POST('/admin/auth/mfa/verify', { body })
      if (error) throw error
      if (!data) throw new Error('Empty response from /admin/auth/mfa/verify')
      return data
    },
  })
}

/**
 * `POST /admin/auth/mfa/resend` — friss kód ugyanarra a challenge-re
 * (DEC-ADMIN-011). A 200 response újra `{ mfa_required, challenge, expires_in }`.
 */
export function useResendMfa() {
  return useMutation({
    mutationFn: async (body: { challenge: string }) => {
      const { data, error } = await apiClient.POST('/admin/auth/mfa/resend', { body })
      if (error) throw error
      if (!data) throw new Error('Empty response from /admin/auth/mfa/resend')
      return data
    },
  })
}

/**
 * `POST /admin/auth/logout` — current admin token revoke (DEC-ADMIN-001).
 *
 * `onSettled` (NEM `onSuccess`) clearSession-t hív, hogy a backend hibája
 * (pl. 401 ha a token már lejárt) se akadályozza a lokális session-tisztítást.
 */
export function useLogout() {
  const { clearSession } = useAuth()
  return useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.POST('/admin/auth/logout')
      if (error) throw error
    },
    onSettled: () => clearSession(),
  })
}
